import { zodResolver } from "@hookform/resolvers/zod";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import InfoIcon from "@mui/icons-material/Info";
import InventoryIcon from "@mui/icons-material/Inventory";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    InputAdornment
} from "@mui/material";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../../components/base/BasicDialog";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import { AddressCopyButton } from "../../../components/buttons/AddressCopyButton";
import { ClientPassportButton } from "../../../components/buttons/ClientPassportButton";
import { AddCompanyForPersonDialog } from "../../../components/dialog/AddCompanyForPersonDialog/AddCompanyForPersonDialog";
import { AddConnectedPersonDialog } from "../../../components/dialog/AddConnectedPersonDialog/AddConnectedPersonDialog";
import { AddReservationDialog } from "../../../components/dialog/AddReservationDialog/AddReservationDialog";
import { ClientFilesDialog } from "../../../components/dialog/ClientFilesDialog/ClientFilesDialog";
import { IdentificationDocumentsButton } from "../../../components/dialog/IdentificationDocumentsDialog";
import { PersonDetailsButton } from "../../../components/dialog/PersonDetailsDialog";
import { BusinessDataFields } from "../../../components/form/BusinessDataFields/BusinessDataFields";
import { FORM_ERR } from "../../../helpers/constants";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useToasts } from "../../../hooks/useToasts";
import {
    RelatedPersonRequestSchema,
    useCustomerById,
    useEditCustomerPersonMutation,
    useRemovePersonReferencyMutation,
} from "../../../services/customersService";
import { useInvestments } from "../../../services/productsService";
import {
    AgreementConfigurationDialog,
    AgreementConfigurationDialogMode,
} from "../../AgreementsPage/AgreementConfigurationDialog/AgreementConfigurationDialog";
import { ClientPassportLeadsTable } from "../../LeadsPage/ClientPassportLeadsTable";
import { LeadAddDialog } from "../../LeadsPage/LeadAddDialog";
import { OfferCreateDialog } from "../../OffersPage/OfferDialogs/OfferCreateDialog";
import { ContactsDialog } from "../../PersonRepositoryPage/ContactsDialog";
import { EditConnectedPersonDialog } from "../../PersonRepositoryPage/EditConnectedPersonDialog";
import { CreateTaskDialog } from "../../TaskPage/CreateTaskDialog";
import { CustomerRelatedCompaniesDropdown } from "../CustomerRelatedCompaniesDropdown";
import { PassportAccordionItem } from "./accordion/PassportAccordionItem";
import { ClientPassportAgreementsTable } from "./tables/ClientPassportAgreementsTable";
import { ClientPassportBookingsTable } from "./tables/ClientPassportBookingsTable";
import { ClientPassportContactsTable } from "./tables/ClientPassportContactsTable";
import { ClientPassportOffersTable } from "./tables/ClientPassportOffersTable";
import { ClientPassportTasksTable } from "./tables/ClientPassportTasksTable";
import { TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH } from "../../../helpers/constants";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { AdditionalPhonesDialog } from "./AdditionalPhonesDialog/AdditionalPhonesDialog";
import MailIcon from '@mui/icons-material/Mail';
import { AdditionalEmailsDialog } from "./AdditionalEmailsDialog/AdditionalEmailsDialog";
import IconButtonWithTooltip from "../../../components/button/IconButtonWithTooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { PersonClausesDialogButton } from "../../../components/dialog/PersonClausesDialog/PersonClausesDialogButton";


export const clientPassportFormSchema = z
    .object({
        // Dane podstawowe
        clientNumber: z.string().optional(),
        firstName: z.string().optional(),
        // cr make first name and last name optional
        // firstName: z.string({ message: FORM_ERR.person_name_required }).nonempty({
        //     message: FORM_ERR.person_name_required,
        // }),
        middleName: z.string().optional(),
        lastName: z.string().optional(),
        // cr make first name and last name optional
        // lastName: z.string({ message: FORM_ERR.surname_required }).nonempty({
        //     message: FORM_ERR.surname_required,
        // }),
        email: z.preprocess(
            (val) => (val === "" ? undefined : val),
            z.string().email(t("form.validations.incorrect_email")).optional().nullable(),
        ),
        phone: z.string().optional(),
        company: z.string().optional(),

        // Adres zamieszkania
        homeStreet: z.string().optional(),
        homeHouseNumber: z.string().optional(),
        homeApartmentNumber: z.string().optional(),
        homeCity: z.string().optional(),
        homePostalCode: z.string().optional(),
        homeCountry: z.string().optional(),

        // Adres korespondencyjny
        correspondenceStreet: z.string().optional(),
        correspondenceHouseNumber: z.string().optional(),
        correspondenceApartmentNumber: z.string().optional(),
        correspondenceCity: z.string().optional(),
        correspondencePostalCode: z.string().optional(),
        correspondenceCountry: z.string().optional(),

        // Dane firmowe
        showJdgFields: z.boolean().optional(),
        jdgNip: z.string().optional(),
        jdgRegon: z.string().optional(),
        jdgCompanyName: z.string().optional(),

        relatedPersons: z.array(RelatedPersonRequestSchema).optional(),

        note: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.homeCountry === "Polska") {
            const phoneRegex = /^\+48\s?\d{3}\s?\d{3}\s?\d{3}$/;
            if (data.phone && !phoneRegex.test(data.phone)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_phone"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const zipCodeRegex = /^\d{2}-\d{3}$/;
            if (data.homePostalCode && !zipCodeRegex.test(data.homePostalCode)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }
            if (
                data.correspondencePostalCode &&
                !zipCodeRegex.test(data.correspondencePostalCode)
            ) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }

            // NIP validation (10 digits)
            const nipRegex = /^\d{10}$/;
            if (data.jdgNip && !nipRegex.test(data.jdgNip)) {
                ctx.addIssue({
                    message: "Niepoprawny format NIP (wymagane 10 cyfr)",
                    code: z.ZodIssueCode.custom,
                });
            }

            // REGON validation (9 or 14 digits)
            const regonRegex = /^(\d{9}|\d{14})$/;
            if (data.jdgRegon && !regonRegex.test(data.jdgRegon)) {
                ctx.addIssue({
                    message: "Niepoprawny format REGON (wymagane 9 lub 14 cyfr)",
                    code: z.ZodIssueCode.custom,
                });
            }
        }
        return z.NEVER;
    });

export function Clauses(props) {
    return (
        <>
            {props.clauses.map((clause, idx) => (
                <Controller
                    key={idx}
                    // @ts-ignore
                    name={`${props.name}.${idx}.decision`}
                    control={props.control}
                    render={({ field }) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...field}
                                    // @ts-ignore
                                    checked={field.value}
                                    defaultChecked={clause.persclau_decision}
                                    sx={{
                                        ...(clause.created ? { pt: 0 } : {}),
                                    }}
                                />
                            }
                            label={
                                <Grid
                                    item
                                    direction={"column"}
                                    sx={{
                                        display: "flex",
                                        alignItems: "start",
                                    }}
                                >
                                    <Typography variant="body2">
                                        {clause.clause_name_eng}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color={"var(--text-muted-foreground)"}
                                    >
                                        {clause.created
                                            ? `Ostatnia zmiana: ${new Date(clause.created).toLocaleString()}`
                                            : ""}
                                    </Typography>
                                </Grid>
                            }
                        />
                    )}
                />
            ))}
        </>
    );
}

export function ClientPassportDialog(props) {
    const { clientId } = props;
    const { data: customerPerson, isLoading, isFetching, refetch } = useCustomerById(clientId);

    const [selectedInvestmentId, setSelectedInvestmentId] = useState(null);

    const { data: investmentsData } = useInvestments();
    const investments = investmentsData?.results || [];

    const customerIsAnonymized = customerPerson?.person?.person_is_anonymized || false
    const customerhasApprovedClause = customerPerson?.has_approved_clause || false

    const title = customerPerson
        ? `${t("clients.ClientPassportDialog.client")}: ${customerPerson.person?.name || ""} ${customerPerson.person?.surname || ""}`
        : t("clients.ClientPassportDialog.client");

    const { handleSubmit, control, formState, register, reset, setValue, watch } = useForm({
        resolver: zodResolver(clientPassportFormSchema),
        defaultValues: {
            company: "test",
        },
    });

    const navigate = useNavigate()

    const { onFormErrors, onServerError } = useToasts();
    const { openDialog: openClientFilesDialog } = useDialogBetter();
    const { openDialog: openAdditionalPhonesForPersonDialog } = useDialogBetter();
    const { openDialog: openAdditionalEmailsForPersonDialog } = useDialogBetter();

    const { mutate: editCustomer } = useEditCustomerPersonMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("clients.passportDialog.successfullyEdited")} />,
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            onServerError(error);
        },
    });
    const { openDialog: openEditConnectedPersonDialog, closeDialog } = useDialogBetter();
    const { openDialog: openNestedPassport, closeDialog: closeNestedPassport } = useDialogBetter();
    const { openDialog: addConnectedPersonDialog } = useDialogBetter();
    const { openDialog: openAddCustomerCompanyDialog } = useDialogBetter();
    const { openDialog: openAddLeadDialogBetter } = useDialogBetter();
    const { openDialog: openContactsDialog } = useDialogBetter();
    const { openDialog: openCreateOfferDialog } = useDialogBetter();
    const { openDialog } = useDialogBetter();

    // Add state for input label shrinking
    const [addressFieldsShrink, setAddressFieldsShrink] = useState({
        correspondenceStreet: false,
        correspondenceHouseNumber: false,
        correspondenceApartmentNumber: false,
        correspondenceCity: false,
        correspondencePostalCode: false,
        correspondenceCountry: false,
    });

    // Add handler for address copying
    const handleAddressCopied = useCallback((values) => {
        setAddressFieldsShrink({
            correspondenceStreet: Boolean(values.correspondenceStreet),
            correspondenceHouseNumber: Boolean(values.correspondenceHouseNumber),
            correspondenceApartmentNumber: Boolean(values.correspondenceApartmentNumber),
            correspondenceCity: Boolean(values.correspondenceCity),
            correspondencePostalCode: Boolean(values.correspondencePostalCode),
            correspondenceCountry: Boolean(values.correspondenceCountry),
        });
    }, []);

    // Update form values when customer data is loaded
    useEffect(() => {
        if (customerPerson?.person) {
            const person = customerPerson.person;
            const livingAddress = person.living_address;
            const mailingAddress = person.mailing_address;

            const firstCompanyId =
                person.related_companies?.length > 0 ? person.related_companies[0].company_id : "";

            // Check if jdg fields exist and should be shown
            const hasBusinessData = !!(person.jdg_nip || person.jdg_regon || person.jdg_name);

            reset({
                clientNumber: String(customerPerson.customer_number || ""),
                firstName: person.name || "",
                middleName: person.name2 || "",
                lastName: person.surname || "",
                email: person.email?.email_address || "",
                phone: person.phone?.phone_number || "",
                company: firstCompanyId,

                // Living address
                homeStreet: livingAddress?.street || "",
                homeHouseNumber: livingAddress?.building || "",
                homeApartmentNumber: livingAddress?.flat_nr || "",
                homeCity: livingAddress?.city || "",
                homePostalCode: livingAddress?.zip_code || "",

                // Mailing address
                correspondenceStreet: mailingAddress?.street || "",
                correspondenceHouseNumber: mailingAddress?.building || "",
                correspondenceApartmentNumber: mailingAddress?.flat_nr || "",
                correspondenceCity: mailingAddress?.city || "",
                correspondencePostalCode: mailingAddress?.zip_code || "",

                // Business fields
                showJdgFields: hasBusinessData,
                jdgNip: person.jdg_nip || "",
                jdgRegon: person.jdg_regon || "",
                jdgCompanyName: person.jdg_name || "",

                note: customerPerson.note || "",
            });
        }
    }, [customerPerson, reset]);

    const onSubmit = (data) => {
        editCustomer({
            customerId: clientId,
            email: data.email ?? undefined,
            person_name: data.firstName,
            person_name2: data.middleName,
            person_surname: data.lastName,
            phone: data.phone,
            address_correspondence: (
                data.correspondenceHouseNumber || 
                data.correspondenceCity || 
                data.correspondenceApartmentNumber || 
                data.correspondenceStreet || 
                data.correspondencePostalCode
             ) ? {
                building: data.correspondenceHouseNumber,
                city: data.correspondenceCity,
                flat_nr: data.correspondenceApartmentNumber,
                street: data.correspondenceStreet,
                zip_code: data.correspondencePostalCode,
            } : null,
            address_living: (
                data.homeHouseNumber || 
                data.homeCity || 
                data.homeApartmentNumber || 
                data.homeStreet || 
                data.homePostalCode
             ) ? {
                building: data.homeHouseNumber,
                city: data.homeCity,
                flat_nr: data.homeApartmentNumber,
                street: data.homeStreet,
                zip_code: data.homePostalCode,
            } : null,
            // Jdg fields
            ...(data.showJdgFields
                ? {
                      jdg_nip: data.jdgNip,
                      jdg_regon: data.jdgRegon,
                      jdg_company_name: data.jdgCompanyName,
                  }
                : {}),
            note: data.note,
            related_persons: data.relatedPersons,
        });
        // submit();
    };

    const { mutate: removePersonReferency } = useRemovePersonReferencyMutation({
        onSuccess: () => {
            toast.custom(
                () => (
                    <SnackbarSuccess message={t("clients.ClientPassportDialog.referencyRemoved")} />
                ),
                {
                    position: "bottom-right",
                },
            );
            refetch();
        },
        onError: (error) => {
            onServerError(error);
        },
    });

    const handleRemovePersonReferency = (personId) => {
        if (window.confirm(t("clients.ClientPassportDialog.confirmRemoveReferency") || "")) {
            if (!customerPerson?.person?.id) {
                return;
            }
            removePersonReferency({
                primary_person_id: customerPerson?.person?.id,
                secondary_person_id: personId,
            });
        }
    };

    if (isLoading || isFetching) {
        return (
            <BasicDialog
                open={props.open}
                onClose={props.onClose}
                titleAlign={"center"}
                contentAlign={"center"}
                title="Loading..."
                maxWidth={"lg"}
                showDialogActions
                alwaysFullScreen={true}
            >
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </BasicDialog>
        );
    }

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            showTitle={false}
            title={title}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={true}
            headerComponent={
                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    sx={{ p: 2 }}
                    // gap={3}
                >   <Grid item xs={4}>
                        <IconButtonWithTooltip
                            title={t("back")}
                            onClick={()=>navigate("/customers/")}
                        >
                            <ArrowBackIcon sx={{ color: "var(--primary)" }} fontSize="large" />
                        </IconButtonWithTooltip>
                    </Grid>
                    <Grid item container direction={"row"} xs={6}>
                        <Typography variant={"h4"} sx={{ fontWeight: "semibold" }}>
                            {title}
                        </Typography>
                        <Grid item sx={{marginLeft: "15px"}}>
                            <Autocomplete
                                sx={{
                                    width: 300,
                                }}
                                size="small"
                                options={[
                                    {
                                        id: null,
                                        investment_name_sales: t(
                                            "clients.ClientPassportDialog.allInvestments",
                                        ),
                                    },
                                    ...investments,
                                ]}
                                value={
                                    selectedInvestmentId
                                        ? investments.find((inv) => inv.id === selectedInvestmentId) ||
                                        null
                                        : {
                                            id: null,
                                            investment_name_sales: t(
                                                "clients.ClientPassportDialog.allInvestments",
                                            ),
                                        }
                                }
                                onChange={(_, newValue) => {
                                    setSelectedInvestmentId(newValue?.id || null);
                                }}
                                autoHighlight
                                getOptionLabel={(option) => option.investment_name_sales}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <Box key={key} {...optionProps} component={"li"}>
                                            {option.investment_name_sales}
                                        </Box>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("clients.ClientPassportDialog.investment")}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={2}></Grid>
                </Grid>
            }
        >
            <Grid container direction={"column"}>
                <Grid container direction={"row"}>
                    <Grid container direction={"column"} xs>
                        <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.ClientPassportDialog.basicDataFormTitle")}
                        </Typography>

                        <Grid container direction={"column"} spacing={2}>
                            <Grid item>
                                <TextField
                                    {...register("clientNumber")}
                                    fullWidth
                                    disabled={true}
                                    label={t("clients.ClientPassportDialog.clientNumber")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("firstName")}
                                    label={t("clients.ClientPassportDialog.firstName")}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("middleName")}
                                    label={t("clients.ClientPassportDialog.middleName")}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("lastName")}
                                    label={t("clients.ClientPassportDialog.lastName")}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("email")}
                                    label={t("clients.ClientPassportDialog.primary_email")}
                                    fullWidth
                                    size="small"
                                    InputProps={customerPerson?.person?.id ? {
                                        endAdornment: 
                                        <InputAdornment position="end">
                                            <IconButton  onClick={() => {
                                                        openAdditionalEmailsForPersonDialog({
                                                            Component: AdditionalEmailsDialog,
                                                            componentProps: {
                                                                personId: customerPerson?.person?.id,
                                                            },
                                                        });
                                                    }}>
                                                <MailIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    } : null}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("phone")}
                                    label={t("clients.ClientPassportDialog.primary_phone")}
                                    fullWidth
                                    size="small"
                                    InputProps={customerPerson?.person?.id ? {
                                        endAdornment: 
                                        <InputAdornment position="end">
                                            <IconButton  onClick={() => {
                                                        openAdditionalPhonesForPersonDialog({
                                                            Component: AdditionalPhonesDialog,
                                                            componentProps: {
                                                                personId: customerPerson?.person?.id,
                                                            },
                                                        });
                                                    }}
                                                    disabled={customerIsAnonymized}
                                                    >
                                                <LocalPhoneIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    } : null}
                                />
                            </Grid>
                            <Grid item>
                                <CustomerRelatedCompaniesDropdown
                                    disabledAddCompany={customerIsAnonymized}
                                    companies={customerPerson?.person?.related_companies || []}
                                    onCompanySelect={(companyId) => {
                                        setValue("company", companyId);
                                    }}
                                    onAddCompany={() => {
                                        openAddCustomerCompanyDialog({
                                            Component: AddCompanyForPersonDialog,
                                            componentProps: {
                                                personId: customerPerson?.person?.id || "",
                                            },
                                            onCloseFn: () => {
                                                refetch();
                                            },
                                        });
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {customerPerson?.person?.id && (
                            <Grid container direction="row" spacing={1} sx={{ mt: 2 }}>
                                <Grid item xs={6}>
                                    <PersonDetailsButton
                                        disabled={customerIsAnonymized}
                                        personId={customerPerson.person.id}
                                        showLabel={true}
                                        variant="contained"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <IdentificationDocumentsButton
                                        disabled={customerIsAnonymized}
                                        personId={customerPerson.person.id}
                                        showLabel={true}
                                        variant="contained"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Grid container direction={"column"} xs>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant={"h6"} sx={{ mb: 2 }}>
                                {t("clients.ClientPassportDialog.addressFormTitle")}
                            </Typography>
                            <AddressCopyButton
                                form={{ watch, setValue }}
                                sourcePrefix="home"
                                targetPrefix="correspondence"
                                fields={[
                                    "Street",
                                    "HouseNumber",
                                    "ApartmentNumber",
                                    "City",
                                    "PostalCode",
                                    "Country",
                                ]}
                                onCopy={handleAddressCopied}
                                tooltipText={t("clients.addPersonDialog.copyFromLivingAddress")}
                                sx={{ ml: 1, mb: 2 }}
                            />
                        </Box>
                        <Grid container direction={"column"} spacing={2}>
                            <Grid item>
                                <TextField
                                    {...register("homeStreet")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.street")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("homeHouseNumber")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.houseNumber")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("homeApartmentNumber")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.apartmentNumber")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("homeCity")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.city")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("homePostalCode")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.postalCode")}
                                    size="small"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("homeCountry")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.country")}
                                    size="small"
                                    defaultValue={t("clients.ClientPassportDialog.poland")}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Grid container direction={"column"} xs>
                        <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.ClientPassportDialog.correspondenceAddressFormTitle")}
                        </Typography>
                        <Grid container direction={"column"} spacing={2}>
                            <Grid item>
                                <TextField
                                    {...register("correspondenceStreet")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.street")}
                                    size="small"
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondenceStreet ||
                                            Boolean(watch("correspondenceStreet")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("correspondenceHouseNumber")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.houseNumber")}
                                    size="small"
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondenceHouseNumber ||
                                            Boolean(watch("correspondenceHouseNumber")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("correspondenceApartmentNumber")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.apartmentNumber")}
                                    size="small"
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondenceApartmentNumber ||
                                            Boolean(watch("correspondenceApartmentNumber")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("correspondenceCity")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.city")}
                                    size="small"
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondenceCity ||
                                            Boolean(watch("correspondenceCity")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("correspondencePostalCode")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.postalCode")}
                                    size="small"
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondencePostalCode ||
                                            Boolean(watch("correspondencePostalCode")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    {...register("correspondenceCountry")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.country")}
                                    size="small"
                                    defaultValue={t("clients.ClientPassportDialog.poland")}
                                    InputLabelProps={{
                                        shrink:
                                            addressFieldsShrink.correspondenceCountry ||
                                            Boolean(watch("correspondenceCountry")),
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <BusinessDataFields
                                    register={register}
                                    control={control}
                                    showFieldsName="showJdgFields"
                                    showInitially={Boolean(
                                        customerPerson?.person?.jdg_nip ||
                                            customerPerson?.person?.jdg_regon ||
                                            customerPerson?.person?.jdg_name,
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                    <Grid container direction={"column"} xs>
                        {/* cr  hide clauses*/}
                        {/* <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.ClientPassportDialog.consents")}
                        </Typography>
                        <Grid
                            container
                            direction={"column"}
                            spacing={2}
                            sx={{ alignItems: "start" }}
                        >
                            <ClausesForm />
                        </Grid>
                        <Divider sx={{ my: 2 }} /> */}
                        <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.ClientPassportDialog.additionalInfo")}
                        </Typography>
                        <Grid container direction={"column"}>
                            <Grid
                                container
                                direction={"row"}
                                justifyContent={"start"}
                                alignItems={"baseline"}
                                gap={2}
                            >
                                <Typography variant={"body1"} sx={{ mt: 1, fontWeight: "medium" }}>
                                    {t("clients.ClientPassportDialog.relatedPersons")}
                                </Typography>
                                <Grid
                                    container
                                    direction={"row"}
                                    justifyContent={"start"}
                                    alignItems={"center"}
                                    gap={1}
                                >
                                    <FormControl sx={{ width: 350 }} size="small">
                                        <InputLabel id="related-persons-label">
                                            {t("clients.ClientPassportDialog.relatedPersons")}
                                        </InputLabel>
                                        <Select
                                            labelId="related-persons-label"
                                            value={
                                                (customerPerson?.person?.related_persons || [])
                                                    .length > 0
                                                    ? `${customerPerson?.person?.related_persons[0].person_id}`
                                                    : "no-related-persons"
                                            }
                                            label={t("clients.ClientPassportDialog.relatedPersons")}
                                            onChange={(event) => {
                                                const personId = event.target.value;
                                                if (personId !== "no-related-persons") {
                                                    const selectedPerson =
                                                        customerPerson?.person?.related_persons.find(
                                                            (p) => p.person_id === personId,
                                                        );
                                                    if (selectedPerson) {
                                                        if (selectedPerson.customer_id) {
                                                            openNestedPassport({
                                                                Component: ClientPassportDialog,
                                                                componentProps: {
                                                                    clientId:
                                                                        selectedPerson.customer_id,
                                                                },
                                                            });
                                                        } else if (selectedPerson.person_id) {
                                                            openEditConnectedPersonDialog({
                                                                Component:
                                                                    EditConnectedPersonDialog,
                                                                componentProps: {
                                                                    personId:
                                                                        selectedPerson.person_id,
                                                                    onSuccessEdit: refetch,
                                                                },
                                                            });
                                                        }
                                                    }
                                                }
                                            }}
                                        >
                                            {(customerPerson?.person?.related_persons || [])
                                                .length === 0 && (
                                                <MenuItem value="no-related-persons">
                                                    -------
                                                </MenuItem>
                                            )}
                                            {(customerPerson?.person?.related_persons || []).map(
                                                (person) => (
                                                    <MenuItem
                                                        key={person.person_id}
                                                        value={person.person_id}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            width: "100%",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography>
                                                                {person.name} {person.surname}
                                                            </Typography>
                                                            {!person.customer_id &&
                                                                person.person_id && (
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openEditConnectedPersonDialog(
                                                                                {
                                                                                    Component:
                                                                                        EditConnectedPersonDialog,
                                                                                    componentProps:
                                                                                        {
                                                                                            personId:
                                                                                                person.person_id,
                                                                                            onSuccessEdit:
                                                                                                refetch,
                                                                                        },
                                                                                },
                                                                            );
                                                                        }}
                                                                    >
                                                                        <InfoIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            {!!person.customer_id && (
                                                                <ClientPassportButton
                                                                    clientId={person.customer_id}
                                                                    clientType="customer"
                                                                    size={"small"}
                                                                />
                                                            )}
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemovePersonReferency(
                                                                        person.person_id,
                                                                    );
                                                                }}
                                                            >
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>
                                    <Grid item xs>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            size="medium"
                                            disabled={customerIsAnonymized}
                                            endIcon={<AddCircleOutlineIcon />}
                                            onClick={() => {
                                                addConnectedPersonDialog({
                                                    Component: AddConnectedPersonDialog,
                                                    componentProps: {
                                                        clientId: clientId,
                                                        onSubmit: (data) => {
                                                            let personToAdd;
                                                            if (data.exisitngClientPersonId) {
                                                                personToAdd = {
                                                                    person_id:
                                                                        data.exisitngClientPersonId,
                                                                    note: data.role || "",
                                                                };
                                                            } else {
                                                                personToAdd = {
                                                                    person: {
                                                                        email: {
                                                                            email_address:
                                                                                data.email || "",
                                                                        },
                                                                        person_name:
                                                                            data.name || "",
                                                                        person_surname:
                                                                            data.surname || "",
                                                                        phone: {
                                                                            phone_number:
                                                                                data.phone || "",
                                                                        },
                                                                    },
                                                                    note: data.role || "",
                                                                };
                                                            }
                                                            setValue("relatedPersons", [
                                                                personToAdd,
                                                            ]);
                                                            handleSubmit(onSubmit, onFormErrors)();
                                                        },
                                                    },
                                                });
                                            }}
                                        >
                                            {t("clients.ClientPassportDialog.add")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Typography
                                variant={"body1"}
                                sx={{
                                    fontWeight: "medium",
                                    mt: 3,
                                    mb: 1,
                                    textAlign: "start",
                                }}
                            >
                                {t("clients.ClientPassportDialog.clientNote")}
                            </Typography>
                            <Grid container justifyContent={"start"}>
                                <TextField
                                    {...register("note")}
                                    fullWidth
                                    label={t("clients.ClientPassportDialog.note")}
                                    InputProps={{
                                        rows: 3,
                                        multiline: true,
                                        inputComponent: "textarea",
                                    }}
                                />
                            </Grid>
                            <Typography
                                variant={"body1"}
                                sx={{
                                    fontWeight: "medium",
                                    mt: 3,
                                    textAlign: "start",
                                }}
                            >
                                {t("clients.ClientPassportDialog.attachments")}
                            </Typography>
                            <Grid container justifyContent={"start"}>
                                <Button
                                    variant="outlined"
                                    startIcon={<InventoryIcon />}
                                    fullWidth
                                    onClick={() => {
                                        openClientFilesDialog({
                                            Component: ClientFilesDialog,
                                            componentProps: {
                                                readOnly:customerIsAnonymized,
                                                clientId: clientId,
                                                clientName:
                                                    `${customerPerson?.person?.name || ""} ${customerPerson?.person?.surname || ""}`.trim(),
                                            },
                                        });
                                    }}
                                >
                                    {t("clients.ClientPassportDialog.viewAttachments")}
                                </Button>
                            <Typography
                                variant={"body1"}
                                sx={{
                                    fontWeight: "medium",
                                    mt: 3,
                                    textAlign: "start",
                                }}
                            >
                                {t("clients.ClientPassportDialog.consents")}
                            </Typography>
                                <PersonClausesDialogButton 
                                    readOnly={customerIsAnonymized} 
                                    customerId={clientId} 
                                    personId={customerPerson?.person?.id} 
                                    customerhasApprovedClause={customerhasApprovedClause}
                                    personName={`${customerPerson?.person?.name || ""} ${customerPerson?.person?.surname || ""}`.trim()}
                                />
                            {!customerhasApprovedClause && <div style={{width: "100%", textAlign: "center"}}>
                                <Typography
                                    variant={"h7"}
                                    color={"error"}
                                >
                                    {`*${t("clients.ClientPassportDialog.noClausesApproved")}`}
                                </Typography>
                            </div>}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={customerIsAnonymized}
                    startIcon={<EditNoteIcon />}
                    onClick={handleSubmit(onSubmit, onFormErrors)}
                >
                    {t("clients.ClientPassportDialog.save")}
                </Button>
                <Divider sx={{ my: 2 }} />
                <Grid container direction={"row"} spacing={2}>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized && customerhasApprovedClause}
                            title={t("clients.ClientPassportDialog.contacts")}
                            onAddClick={() => {
                                openContactsDialog({
                                    Component: ContactsDialog,
                                    componentProps: {
                                        personId: customerPerson?.person?.id || "",
                                    },
                                });
                            }}
                        >
                            <ClientPassportContactsTable
                                personId={customerPerson?.person?.id || ""}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized}
                            title={t("clients.ClientPassportDialog.leads")}
                            onAddClick={() => {
                                openAddLeadDialogBetter({
                                    Component: LeadAddDialog,
                                    componentProps: {
                                        defaultData: {
                                            firstName: customerPerson?.person?.name || "",
                                            lastName: customerPerson?.person?.surname || "",
                                            email:
                                                customerPerson?.person?.email?.email_address || "",
                                            phone:
                                                customerPerson?.person?.phone?.phone_number || "",
                                        },
                                        disablePersonFields: true,
                                        personId: customerPerson?.person?.id || "",
                                        onSuccessEdit: () => {
                                            refetch();
                                        },
                                    },
                                });
                            }}
                        >
                            <ClientPassportLeadsTable
                                personId={customerPerson?.person?.id || ""}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                </Grid>

                <Grid container direction={"row"} spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized}
                            title={t("clients.ClientPassportDialog.bookings")}
                            onAddClick={() => {
                                openDialog({
                                    Component: AddReservationDialog,
                                    componentProps: {
                                        initialCustomerIds: [clientId],
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportBookingsTable
                                customerId={clientId}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized && customerhasApprovedClause}
                            title={t("clients.ClientPassportDialog.offers")}
                            onAddClick={() => {
                                openCreateOfferDialog({
                                    Component: OfferCreateDialog,
                                    componentProps: { initialCustomerIds: [clientId] },
                                });
                            }}
                        >
                            <ClientPassportOffersTable
                                customerId={clientId}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized}
                            title={t("clients.ClientPassportDialog.agreements")}
                            onAddClick={() => {
                                openDialog({
                                    Component: AgreementConfigurationDialog,
                                    componentProps: {
                                        mode: AgreementConfigurationDialogMode.CREATE,
                                        initialCustomerIds: [clientId],
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportAgreementsTable
                                customerId={clientId}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                    <Grid item xs={6}>
                        <PassportAccordionItem
                            showAddButton={!customerIsAnonymized}
                            title={t("clients.ClientPassportDialog.tasks")}
                            onAddClick={() => {
                                openDialog({
                                    Component: CreateTaskDialog,
                                    componentProps: {
                                        customerId: clientId || "",
                                    },
                                    onCloseFn: () => {
                                        refetch();
                                    },
                                });
                            }}
                        >
                            <ClientPassportTasksTable
                                relatedTasksForClientId={props.clientId || ""}
                                customerId={clientId || ""}
                                investmentId={selectedInvestmentId}
                                colMaxLength={TABLE_TEXT_FIELD_MAX_EXTEND_LENGTH}
                            />
                        </PassportAccordionItem>
                    </Grid>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
